/**
 * DomAnalyzer — Smart DOM Analysis inspired by Alibaba's PageAgent.
 *
 * Traverses the host page DOM, identifies interactive and structural elements,
 * assigns numeric indices, and produces a text representation suitable for
 * an LLM to reason about.
 *
 * The element map is kept so actions can resolve indices back to HTMLElements.
 */

// Tags that are inherently interactive
const INTERACTIVE_TAGS = new Set([
  'a', 'button', 'input', 'select', 'textarea',
  'details', 'summary', 'label', 'option',
])

// ARIA roles that indicate interactivity
const INTERACTIVE_ROLES = new Set([
  'button', 'link', 'menuitem', 'menuitemcheckbox', 'menuitemradio',
  'tab', 'switch', 'slider', 'combobox', 'listbox', 'option',
  'radio', 'checkbox', 'textbox', 'searchbox', 'scrollbar',
])

// Heading tags (indexed for scroll-to targets, not clickable)
const HEADING_TAGS = new Set(['h1', 'h2', 'h3', 'h4', 'h5', 'h6'])

// Attributes worth including in the text representation
const USEFUL_ATTRS = [
  'type', 'role', 'aria-label', 'placeholder', 'value',
  'href', 'alt', 'title', 'name', 'checked', 'aria-expanded',
]

export interface AnalysisResult {
  text: string
  elementMap: Map<number, HTMLElement>
}

export class DomAnalyzer {
  private elementMap = new Map<number, HTMLElement>()

  /** Analyze the current page and return a text snapshot + element map. */
  analyze(): AnalysisResult {
    this.elementMap.clear()
    let index = 0
    const lines: string[] = []

    // Page header
    lines.push(`Page: ${document.title}`)
    lines.push(`URL: ${location.href}`)
    lines.push('')

    const walk = (node: Node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = (node.textContent ?? '').trim()
        if (text && text.length > 1) {
          lines.push(text)
        }
        return
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return

      const el = node as HTMLElement
      const tag = el.tagName.toLowerCase()

      // Skip invisible elements
      if (!this.isVisible(el)) return

      // Skip script, style, svg, noscript, the widget itself
      if (['script', 'style', 'svg', 'noscript', 'link', 'meta'].includes(tag)) return
      if (el.id === 'holostaff-widget-root' || el.closest('#holostaff-widget-root')) return

      const isInteractive = this.isInteractive(el)
      const isHeading = HEADING_TAGS.has(tag)

      if (isInteractive || isHeading) {
        const idx = index++
        this.elementMap.set(idx, el)
        const attrs = this.getUsefulAttrs(el)
        const attrStr = attrs ? ' ' + attrs : ''
        const innerText = this.getDirectText(el).slice(0, 200)

        if (isHeading) {
          // Render headings with markdown-style markers
          const level = parseInt(tag[1])
          const hashes = '#'.repeat(level)
          lines.push('')
          lines.push(`${hashes} [${idx}]${innerText}`)
        } else {
          lines.push(`[${idx}]<${tag}${attrStr}>${innerText}</${tag}>`)
        }
        return // don't recurse into interactive elements (text already captured)
      }

      // For non-interactive elements, recurse into children
      for (const child of el.childNodes) {
        walk(child)
      }
    }

    walk(document.body)

    return { text: lines.join('\n'), elementMap: this.elementMap }
  }

  /** Get an element by its analysis index. */
  getElementByIndex(index: number): HTMLElement | null {
    return this.elementMap.get(index) ?? null
  }

  // -----------------------------------------------------------------------
  // Private helpers
  // -----------------------------------------------------------------------

  private isVisible(el: HTMLElement): boolean {
    // Quick checks before expensive getComputedStyle
    if (el.offsetWidth === 0 && el.offsetHeight === 0) {
      // Could be inline elements — check bounding rect
      const rect = el.getBoundingClientRect()
      if (rect.width === 0 && rect.height === 0) return false
    }

    const style = getComputedStyle(el)
    if (style.display === 'none') return false
    if (style.visibility === 'hidden') return false
    if (style.opacity === '0') return false

    return true
  }

  private isInteractive(el: HTMLElement): boolean {
    const tag = el.tagName.toLowerCase()

    // Layer 1: Tag-based
    if (INTERACTIVE_TAGS.has(tag)) {
      // Skip disabled elements
      if ((el as HTMLInputElement).disabled) return false
      return true
    }

    // Layer 2: Role-based
    const role = el.getAttribute('role')
    if (role && INTERACTIVE_ROLES.has(role)) return true

    // Layer 3: Cursor-based
    try {
      const cursor = getComputedStyle(el).cursor
      if (cursor === 'pointer') return true
    } catch {}

    // Layer 4: tabindex (explicitly focusable)
    if (el.hasAttribute('tabindex') && el.getAttribute('tabindex') !== '-1') return true

    // Layer 5: contenteditable
    if (el.isContentEditable && el.getAttribute('contenteditable') !== 'false') return true

    return false
  }

  private getDirectText(el: HTMLElement): string {
    // Get text content, collapse whitespace
    const raw = el.textContent ?? ''
    return raw.replace(/\s+/g, ' ').trim()
  }

  private getUsefulAttrs(el: HTMLElement): string {
    const parts: string[] = []
    for (const attr of USEFUL_ATTRS) {
      const val = el.getAttribute(attr)
      if (val && val.length > 0 && val.length < 100) {
        parts.push(`${attr}="${val}"`)
      }
    }
    return parts.join(' ')
  }
}
